import type { Notification } from "types/notification.types";

export interface NotificationBucket {
	key: "today" | "week" | "earlier";
	label: string;
	items: Notification[];
}

const startOfDay = (d: Date): Date =>
	new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * Group a notification feed into Today / This week / Earlier time buckets
 * (design §3). Empty buckets are omitted, and bucket order is fixed
 * (today → week → earlier). Order WITHIN a bucket is preserved from the input
 * (the feed already arrives newest-first), so a bucket renders newest at top.
 *
 *   Today   = on the current calendar day
 *   Week    = the previous 6 calendar days (not today)
 *   Earlier = older than that
 *
 * `now` is injectable so the grouping is deterministic in tests.
 */
export function groupNotificationsByTime(
	notifications: Notification[],
	now: Date = new Date(),
): NotificationBucket[] {
	const todayStart = startOfDay(now);
	const weekStart = new Date(todayStart);
	weekStart.setDate(weekStart.getDate() - 6);

	const today: Notification[] = [];
	const week: Notification[] = [];
	const earlier: Notification[] = [];

	for (const n of notifications) {
		const t = new Date(n.createdAt ?? n.date);
		if (t >= todayStart) today.push(n);
		else if (t >= weekStart) week.push(n);
		else earlier.push(n);
	}

	const buckets: NotificationBucket[] = [
		{ key: "today", label: "Today", items: today },
		{ key: "week", label: "This week", items: week },
		{ key: "earlier", label: "Earlier", items: earlier },
	];
	return buckets.filter((b) => b.items.length > 0);
}
